import Request from 'components/Request';
import { gameTypeClick } from 'react/redux/actions/matchSelection';
import { setActiveSearchType } from 'react/redux/actions/matchmaking';
import { savePreferences } from 'react/settings/AccountSettings/accountSettingsRequests';
import { searchOptionsForMode, NEARBY_ONLY, PREFER_NEARBY, RANKED } from 'react/matchmaking/findMatch/findMatchModes';
import { __ } from 'i18n/index';
import { showToast } from 'react/toasts/showToast';

export const FIND_MATCH_SETTINGS_LOADED = 'FIND_MATCH_SETTINGS_LOADED';
export const MODE_SETTINGS_SAVED = 'MODE_SETTINGS_SAVED';
export const FIND_MATCH_CONNECTION_TYPE_SAVED = 'FIND_MATCH_CONNECTION_TYPE_SAVED';
export const FIND_MATCH_LADDER_SELECTED = 'FIND_MATCH_LADDER_SELECTED';
export const FIND_MATCH_MODE_STARTED = 'FIND_MATCH_MODE_STARTED';

// my_user from startup carries mode_settings, connection_type and the
// player's ranked 2v2 party (doubles_party; reducers/doublesParty).
export const findMatchSettingsLoaded = (myUserData) => ({
	type: FIND_MATCH_SETTINGS_LOADED,
	payload: {
		modeSettings: myUserData.mode_settings,
		connectionType: myUserData.connection_type,
		playerId: myUserData.id,
		doublesParty: myUserData.doubles_party,
	},
});

export const selectFindMatchLadder = (ladderId) => ({
	type: FIND_MATCH_LADDER_SELECTED,
	payload: ladderId,
});

/**
 * Starts the mode's search on one ladder with the mode's saved options.
 * gameTypeClick asks for a connection type when the server needs one;
 * onRefused receives the refused searches ({ ladderId, message,
 * duplicateSearch, searchId }) instead of a toast.
 */
export const startModeSearch = (mode, ladderId, onRefused = null) => async (dispatch, getState) => {
	const { findMatch, matchmaking } = getState();
	const playOption = searchOptionsForMode(mode, findMatch.modeSettings, ladderId);

	if(matchmaking.currentMatch) {
		showToast({ content: __('Exiting previous match...') });
		await matchmaking.currentMatch.closeMatch().catch(() => {});
	}

	dispatch(setActiveSearchType(mode === RANKED ? 'ranked' : 'friendlies'));
	dispatch({ type: FIND_MATCH_MODE_STARTED, payload: mode });
	return dispatch(gameTypeClick([playOption], null, playOption.queueVisibilityType, onRefused));
};

/**
 * Saves one mode's settings. draft is { nearbyOnly, gameCount,
 * queueType } plus, when the dialog changed the opponent rank, rankLadderId
 * and rank ({ name_id, direction, division }, or null for any rank). Ranked
 * leaves out its length and matching method: the server fixes them. Resolves
 * with every mode's settings; rejects with the server response.
 */
export const saveModeSettings = (mode, draft) => (dispatch) => {
	const parameters = { mode, distance_severity_id: draft.nearbyOnly ? NEARBY_ONLY : PREFER_NEARBY };
	if(mode !== RANKED) {
		parameters.match_count = draft.gameCount;
		parameters.queue_type = draft.queueType;
	}
	if(draft.rankLadderId) {
		parameters.ladder_id = draft.rankLadderId;
		if(draft.rank) {
			parameters.tier_id = draft.rank.name_id;
			parameters.direction = draft.rank.direction;
			parameters.division = draft.rank.division ?? null;
		} else {
			parameters.direction = 0;
		}
	}
	return Promise.resolve(Request.apiv1('matchmaking/save_mode_settings', parameters))
		.then((response) => {
			dispatch({ type: MODE_SETTINGS_SAVED, payload: response.mode_settings });
			return response.mode_settings;
		});
};

// Connection type is the account preference, not a mode setting.
export const saveConnectionType = (connectionType) => (dispatch) => savePreferences({ connection_type: connectionType })
	.then((preferences) => {
		dispatch({ type: FIND_MATCH_CONNECTION_TYPE_SAVED, payload: connectionType });
		return preferences;
	});
